// Lightbox — full-screen overlay viewer for the gallery.
// Click a tile → opens here instead of navigating to /media/:id.
// Keyboard: ← / → / Esc.   Pointer: prev / next / close buttons + backdrop tap.
// Adjacent items are preloaded for snappy navigation.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowRight, X as XIcon, Smartphone, Download, MessageSquare, User, Star } from "lucide-react";
import { toast } from "sonner";
import { Media } from "../lib/api";
import { saveItemsToDevice, canWebShareFiles } from "../lib/download";

export default function Lightbox({
  items,              // ordered array of media objects currently in the lightbox
  startId,            // id of the item to open at
  onClose,
  slug,
  isHost = false,
  onSetCover,         // host-only: called with media id to mark as album cover
  coverMediaId,       // currently-set cover id
}) {
  const startIndex = useMemo(() => {
    const i = items.findIndex((m) => m.id === startId);
    return i >= 0 ? i : 0;
  }, [items, startId]);
  const [index, setIndex] = useState(startIndex);
  const [imageLoading, setImageLoading] = useState(true);
  const [savingDevice, setSavingDevice] = useState(false);
  const canSaveToCameraRoll = useMemo(() => canWebShareFiles(), []);

  // Reset state when the lightbox is reopened with a different start id
  useEffect(() => { setIndex(startIndex); }, [startIndex]);

  const current = items[index];
  const hasPrev = index > 0;
  const hasNext = index < items.length - 1;

  const goPrev = useCallback(() => {
    if (!hasPrev) return;
    setIndex((i) => i - 1);
    setImageLoading(true);
  }, [hasPrev]);
  const goNext = useCallback(() => {
    if (!hasNext) return;
    setIndex((i) => i + 1);
    setImageLoading(true);
  }, [hasNext]);

  // Keyboard
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
      if (e.key === "ArrowLeft") { e.preventDefault(); goPrev(); return; }
      if (e.key === "ArrowRight") { e.preventDefault(); goNext(); return; }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, goPrev, goNext]);

  // Lock body scroll
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  // Pre-cache adjacent thumbs/images so flipping forward feels instant
  useEffect(() => {
    const preload = (m) => {
      if (!m || m.is_video) return;
      const img = new window.Image();
      img.src = Media.previewUrl(m.id);
    };
    preload(items[index + 1]);
    preload(items[index - 1]);
  }, [items, index]);

  // Swipe gestures on touch — left/right swipe moves to next/prev.
  const touchStartRef = React.useRef(null);
  const onTouchStart = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    touchStartRef.current = { x: t.clientX, y: t.clientY, time: Date.now() };
  };
  const onTouchEnd = (e) => {
    const start = touchStartRef.current;
    if (!start) return;
    touchStartRef.current = null;
    const t = e.changedTouches?.[0];
    if (!t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      if (dx < 0) goNext(); else goPrev();
    }
  };

  const handleSaveToDevice = async () => {
    if (!current || savingDevice) return;
    setSavingDevice(true);
    const tid = toast.loading(
      canSaveToCameraRoll
        ? (current.is_video ? "Preparing video…" : "Preparing photo…")
        : "Downloading…"
    );
    try {
      const result = await saveItemsToDevice([current]);
      toast.dismiss(tid);
      if (result.ok) {
        toast.success(result.mode === "share"
          ? (current.is_video ? "Tap Save Video to Photos" : "Tap Save Image to Photos")
          : "Saved to your downloads");
      } else if (result.reason !== "cancelled") {
        toast.error("Save failed — try again");
      }
    } catch (_) {
      toast.dismiss(tid);
      toast.error("Save failed — try again");
    } finally { setSavingDevice(false); }
  };

  if (!current) return null;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center"
      style={{ background: "rgba(4,3,12,0.94)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      data-testid="lightbox"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Decorative auras */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 -right-40 w-[480px] h-[480px] rounded-full"
        style={{ background: "radial-gradient(closest-side, rgba(127,75,255,0.22), transparent 70%)" }}
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-40 -left-40 w-[480px] h-[480px] rounded-full"
        style={{ background: "radial-gradient(closest-side, rgba(34,226,255,0.18), transparent 70%)" }}
      />

      {/* Top bar */}
      <div
        className="absolute top-0 left-0 right-0 flex items-center justify-between gap-3 px-4 sm:px-6 py-3"
        onClick={(e) => e.stopPropagation()}
        style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.55), transparent)" }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
            style={{
              background: "rgba(127,75,255,0.20)",
              border: "1px solid rgba(127,75,255,0.45)",
              color: "#C9B6FF",
            }}
            data-testid="lightbox-index"
          >
            {index + 1} / {items.length}
          </span>
          <div className="min-w-0 hidden sm:block">
            <div className="flex items-center gap-1 text-[12px]" style={{ color: "rgba(255,255,255,0.85)" }}>
              <User size={11} color="rgba(255,255,255,0.65)" />
              <span className="truncate" style={{ maxWidth: 260 }}>{current.uploader_name || "Guest"}</span>
            </div>
            <div className="text-[10px] truncate" style={{ color: "rgba(255,255,255,0.50)", maxWidth: 320 }}>
              {current.title || current.filename || "Untitled"}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {isHost && !current.is_video && (
            <button
              type="button"
              onClick={() => onSetCover && onSetCover(current.id)}
              className="meye-chip"
              style={{
                padding: "8px 12px",
                fontSize: 12,
                background: coverMediaId === current.id
                  ? "linear-gradient(135deg, rgba(255,196,0,0.30), rgba(255,107,53,0.20))"
                  : "rgba(255,255,255,0.05)",
                borderColor: coverMediaId === current.id ? "rgba(255,196,0,0.65)" : "rgba(255,255,255,0.15)",
                color: coverMediaId === current.id ? "#FFD24D" : "#fff",
              }}
              data-testid="lightbox-set-cover"
              title={coverMediaId === current.id ? "This is the album cover" : "Set as album cover"}
            >
              <Star size={13} fill={coverMediaId === current.id ? "#FFD24D" : "none"} />
              <span className="hidden sm:inline">{coverMediaId === current.id ? "Cover" : "Set cover"}</span>
            </button>
          )}
          <button
            type="button"
            onClick={handleSaveToDevice}
            disabled={savingDevice}
            className="meye-chip"
            style={{
              padding: "8px 12px",
              fontSize: 12,
              background: "linear-gradient(135deg, rgba(127,75,255,0.18), rgba(34,226,255,0.18))",
              borderColor: "rgba(34,226,255,0.55)",
              color: "#fff",
            }}
            data-testid="lightbox-save"
            title={canSaveToCameraRoll ? "Save to camera roll" : "Download"}
          >
            {canSaveToCameraRoll ? <Smartphone size={13} /> : <Download size={13} />}
            <span className="hidden sm:inline">{canSaveToCameraRoll ? "Save" : "Download"}</span>
          </button>
          <a
            href={`/${slug}/media/${current.id}`}
            onClick={(e) => e.stopPropagation()}
            className="meye-chip"
            style={{
              padding: "8px 12px",
              fontSize: 12,
              background: "rgba(127,75,255,0.14)",
              borderColor: "rgba(127,75,255,0.50)",
              color: "#C9B6FF",
            }}
            data-testid="lightbox-open-page"
            title="Open comments"
          >
            <MessageSquare size={13} />
            <span className="hidden sm:inline">Comments</span>
          </a>
          <button
            onClick={onClose}
            className="rounded-full p-2 transition hover:bg-white/10"
            style={{ background: "rgba(255,255,255,0.06)" }}
            data-testid="lightbox-close"
            aria-label="Close"
          >
            <XIcon size={18} color="#fff" />
          </button>
        </div>
      </div>

      {/* Prev / Next */}
      {hasPrev && (
        <button
          onClick={(e) => { e.stopPropagation(); goPrev(); }}
          className="absolute left-3 sm:left-6 top-1/2 -translate-y-1/2 rounded-full p-3 transition hover:scale-110 active:scale-95"
          style={{
            background: "rgba(15,12,28,0.85)",
            border: "1px solid rgba(127,75,255,0.45)",
            boxShadow: "0 16px 40px -12px rgba(127,75,255,0.45)",
          }}
          data-testid="lightbox-prev"
          aria-label="Previous"
        >
          <ArrowLeft size={20} color="#fff" />
        </button>
      )}
      {hasNext && (
        <button
          onClick={(e) => { e.stopPropagation(); goNext(); }}
          className="absolute right-3 sm:right-6 top-1/2 -translate-y-1/2 rounded-full p-3 transition hover:scale-110 active:scale-95"
          style={{
            background: "rgba(15,12,28,0.85)",
            border: "1px solid rgba(34,226,255,0.45)",
            boxShadow: "0 16px 40px -12px rgba(34,226,255,0.45)",
          }}
          data-testid="lightbox-next"
          aria-label="Next"
        >
          <ArrowRight size={20} color="#fff" />
        </button>
      )}

      {/* Media canvas */}
      <div
        className="relative max-w-[94vw] max-h-[88vh] flex items-center justify-center"
        onClick={(e) => e.stopPropagation()}
        key={current.id} // remount on item change so video gets fresh state
      >
        {current.is_video ? (
          <video
            src={Media.previewUrl(current.id)}
            poster={Media.thumbUrl(current.id)}
            controls
            autoPlay
            playsInline
            preload="metadata"
            style={{ maxWidth: "94vw", maxHeight: "84vh", background: "#000", borderRadius: 12 }}
            data-testid="lightbox-video"
          />
        ) : (
          <>
            {imageLoading && (
              <div className="absolute inset-0 flex items-center justify-center" data-testid="lightbox-loading">
                <div className="meye-spinner" />
              </div>
            )}
            <img
              src={Media.previewUrl(current.id)}
              alt={current.title || current.filename}
              onLoad={() => setImageLoading(false)}
              onError={() => setImageLoading(false)}
              style={{
                maxWidth: "94vw",
                maxHeight: "84vh",
                objectFit: "contain",
                borderRadius: 12,
                boxShadow: "0 30px 80px -20px rgba(127,75,255,0.45), 0 24px 60px -28px rgba(34,226,255,0.45)",
                opacity: imageLoading ? 0 : 1,
                transition: "opacity 200ms",
              }}
              data-testid="lightbox-image"
            />
          </>
        )}
      </div>

      {/* Bottom hint — keyboard arrows */}
      <div
        className="absolute bottom-4 left-1/2 -translate-x-1/2 hidden sm:flex items-center gap-2 text-[11px]"
        style={{ color: "rgba(255,255,255,0.45)" }}
      >
        <kbd className="meye-kbd">←</kbd><kbd className="meye-kbd">→</kbd>
        <span>navigate</span>
        <span style={{ opacity: 0.4 }}>·</span>
        <kbd className="meye-kbd">Esc</kbd>
        <span>close</span>
      </div>
    </div>
  );
}
